// @ts-check

/**
 * @file Shared automation `agent_state` labels and mark-ready eligibility.
 */

import _ from "gettext-universal/build/src/translate.js"

/** Business `state` value a task must hold to be queued for automation. */
const OPEN_TASK_STATE = "open"

/**
 * `agent_state` values from which a task can be (re-)queued as agent-ready,
 * matching the backend `markAgentReady` transition guard. A null/blank state
 * (not under automation), plus the parked `blocked` and `done` states, may be
 * queued; a task a worker is actively holding may not.
 * @type {Set<string>}
 */
const MARK_READY_ELIGIBLE_AGENT_STATES = new Set(["", "blocked", "done"])

/**
 * Translates an automation `agent_state` for display. A null/blank value means
 * the task is not under automation.
 * @param {string | null | undefined} agentState Automation state value.
 * @returns {string} Translated agent-state label.
 */
export function translatedAgentState(agentState) {
  if (!agentState) return _("Not under automation")
  if (agentState === "agent_ready") return _("Agent ready")
  if (agentState === "claimed") return _("Claimed")
  if (agentState === "asking_question") return _("Asking question")
  if (agentState === "coding") return _("Coding")
  if (agentState === "verifying") return _("Verifying")
  if (agentState === "review") return _("In review")
  if (agentState === "blocked") return _("Blocked")
  if (agentState === "done") return _("Done")
  return agentState
}

/**
 * Translates the public runtime status for display.
 * @param {string | null | undefined} runtimeStatus Execution-state code from the Task model.
 * @returns {string | null} Localized execution-state label or null.
 */
export function translatedAutomationRuntimeStatus(runtimeStatus) {
  if (!runtimeStatus) return null
  if (runtimeStatus === "queued") return _("Queued")
  if (runtimeStatus === "asking") return _("Waiting for reply")
  if (runtimeStatus === "coding") return _("Coding")
  if (runtimeStatus === "verifying") return _("Verifying")
  if (runtimeStatus === "review") return _("In review")
  if (runtimeStatus === "blocked") return _("Blocked")
  if (runtimeStatus === "done") return _("Done")
  return runtimeStatus
}

/**
 * Whether a task may be marked agent-ready from the UI. Mirrors the backend
 * guard so the affordance is only offered when the action will succeed: the
 * task must be open and either not under automation yet or parked in a
 * re-queueable state, never while a worker is actively holding it. It also
 * requires the project to have automation assignment enabled and mapped before exposing the
 * action.
 * @param {{agentState: string | null | undefined, taskState: string | null | undefined, automationAssignable: boolean | null | undefined}} task Current task states and project assignment eligibility.
 * @returns {boolean} True when a mark-agent-ready action should be offered.
 */
export function canMarkTaskAgentReady({agentState, taskState, automationAssignable = true}) {
  if (taskState !== OPEN_TASK_STATE) return false
  if (automationAssignable === false) return false
  if (agentState === null || agentState === undefined) return true

  return MARK_READY_ELIGIBLE_AGENT_STATES.has(agentState)
}

/**
 * Whether the automation runtime is waiting for a human task-owner decision.
 * @param {{agentState: string | null | undefined, runtimeStatus: string | null | undefined}} task Current automation state values.
 * @returns {boolean} True when owner approval/comment controls should be offered.
 */
export function canRespondToAutomationPlan({agentState, runtimeStatus}) {
  return agentState === "asking_question" || runtimeStatus === "asking"
}

/**
 * Returns compact board-visible automation status for a task card.
 * @param {string | null | undefined} agentState Automation state value.
 * @param {string | null | undefined} runtimeStatus Runtime status value.
 * @returns {string | null} Short status label.
 */
export function boardAutomationStatusLabel(agentState, runtimeStatus = null) {
  const translatedRuntimeStatus = translatedAutomationRuntimeStatus(runtimeStatus)

  if (translatedRuntimeStatus) return translatedRuntimeStatus
  if (!agentState) return null
  if (agentState === "claimed") return _("AI assigned")
  if (agentState === "coding" || agentState === "verifying" || agentState === "review") return _("AI running")
  if (agentState === "agent_ready" || agentState === "asking_question") return _("AI waiting")
  return translatedAgentState(agentState)
}
